import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import AnalysisListCard from './components/AnalysisListCard';
import InfoCard from './components/InfoCard';
import { Button, Col, DatePicker, Row, Select, Spin } from 'antd';
import dayjs from 'dayjs';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import StatCard from '../../components/statCard/StatCard';
import { Cart } from 'react-bootstrap-icons';
import { VscInspect } from 'react-icons/vsc';
import CustomTabs from '../../components/tabs/CustomTabs';
import { useNavigate } from 'react-router-dom';
import TrendExplorerTab from './tabs/TrendExplorerTab';
import OverviewTab from './tabs/OverviewTab';
import ComparativeTab from './tabs/ComparativeTab';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import { analysisService } from '../../api/services/analysisService';
import { inspectionService } from '../../api/services/inspectionService';
import type { Basket } from '../../types/basket';
import type { Location } from '../../types/referenceData';
import PriceMapTab from './tabs/PriceMapTab';
import { normalize } from '../../utils/normalizeWords';
import { PercentageOutlined } from '@ant-design/icons';
import { disableFutureDates } from '../../utils/disableDates';
import ProductPriceAnalyticsTab from './tabs/ProductPriceAnalyticsTab';
import { useIsOnlineContext } from '../../contexts/IsOnlineContext';
import ExternalDataUploadTab from './tabs/ExternalDataUploadTab';
import { usePermissions } from '../../hooks/usePermissions';
import { PermissionEnum } from '../../enums/PermissionEnum';

const AnalysisAndReports = () => {
  const { t } = useTranslation(['analysisAndReports', 'dataUpload']);
  const navigate = useNavigate();
  const [baskets, setBaskets] = useState<Basket[]>([]);
  const [presentations, setPresentations] = useState<any>();
  const [locations, setLocations] = useState<Location[]>([]);
  const [municipalities, setMunicipalities] = useState<Location[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [municipalityLoading, setMunicipalityLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, 'day').startOf('day'),
    dayjs().endOf('day'),
  ]);
  const [selectedBasket, setSelectedBasket] = useState<number | undefined>(undefined);
  const [defaultBasketId, setDefaultBasketId] = useState<number | undefined>(undefined);
  const [selectedPresentations, setSelectedPresentations] = useState<number[]>([]);
  const [selectedBasketLabel, setSelectedBasketLabel] = useState<string>('');
  const [selectedMunicipality, setSelectedMunicipality] = useState<string | undefined>(undefined);
  const [averagePrice, setAveragePrice] = useState<number>(0);
  const [averagePriceLoading, setAveragePriceLoading] = useState(false);
  const [priceChange, setPriceChange] = useState<number>(0);
  const [priceChangeLoading, setPriceChangeLoading] = useState(false);
  const [priceChangeDirection, setPriceChangeDirection] = useState<
    'increase' | 'decrease' | 'no-change'
  >('no-change');
  const [inspectionCount, setInspectionCount] = useState<number>(0);
  const [inspectionCountLoading, setInspectionCountLoading] = useState(false);

  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [enableClearFiltersBtn, setEnableClearFiltersBtn] = useState<boolean>(false);

  const [previousPeriodEndDate, setPreviousPeriodEnd] = useState<any>();
  const [previousPeriodStartDate, setPreviousPeriodStart] = useState<any>();

  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('1');

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  // calculation of last period for change of average price info tooltip
  const calculateLastPeriod = () => {
    const currentPeriodDuration = dateRange[1].valueOf() - dateRange[0].valueOf();

    const previousPeriodEnd = dateRange[0].valueOf();
    const previousPeriodStart = dateRange[0].valueOf() - currentPeriodDuration;

    setPreviousPeriodEnd(dayjs(previousPeriodEnd).format('YYYY-MM-DD'));
    setPreviousPeriodStart(dayjs(previousPeriodStart).format('YYYY-MM-DD'));
  };

  const [inflationProducts, setInflationProducts] = useState<
    Array<{
      productName?: string;
      priceChangePercentage?: number;
      type?: string;
      priceCount?: number;
      priceArray?: number[];
      baskets?: Array<{
        refId: string;
        name: string;
      }>;
    }>
  >([]);

  const [deflationProducts, setDeflationProducts] = useState<
    Array<{
      productName?: string;
      priceChangePercentage?: number;
      type?: string;
      priceCount?: number;
      priceArray?: number[];
      baskets?: Array<{
        refId: string;
        name: string;
      }>;
    }>
  >([]);
  const [inflationLoading, setInflationLoading] = useState(false);
  const [deflationLoading, setDeflationLoading] = useState(false);

  const { isOnline } = useIsOnlineContext();
  const selectedBasketName =
    baskets.find((basket) => basket.id === selectedBasket)?.name || selectedBasketLabel;

  useEffect(() => {
    const loadBaskets = async () => {
      try {
        setLoading(true);
        const basketsData = await referenceDataSyncService.getBaskets();
        setBaskets(basketsData as unknown as Basket[]);
        const basicBasket = await referenceDataSyncService.getBasicFoodBasket();
        setDefaultBasketId(basicBasket?.id);
        setSelectedBasket(basicBasket?.id);
        setSelectedBasketLabel(basicBasket?.name || '');
      } catch (error) {
        console.error('Failed to load baskets:', error);
      } finally {
        setLoading(false);
      }
    };

    const loadLocations = async () => {
      try {
        setLocationLoading(true);
        const locationsData = await referenceDataSyncService.getLocations();
        setLocations(locationsData as unknown as Location[]);
      } catch (error) {
        console.error('Failed to load locations:', error);
      } finally {
        setLocationLoading(false);
      }
    };

    loadBaskets();
    loadLocations();
    loadPresentationsForSelection();
  }, []);

  const clearAllFilters = () => {
    setSelectedPresentations([]);
    setSelectedDepartment(undefined);
    setSelectedMunicipality(undefined);
    setSelectedBasket(defaultBasketId);
    setSelectedLocationIds([]);
    setDateRange([dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')]);
    calculateLastPeriod();
  };

  const fetchAveragePrice = async () => {
    if (!selectedBasket) return;
    try {
      setAveragePriceLoading(true);

      const params = {
        locationIds: selectedLocationIds,
        basketId: selectedBasket || undefined,
        startTime: dateRange[0].startOf('day').valueOf(),
        endTime: dateRange[1].endOf('day').valueOf(),
        productIds: selectedPresentations || undefined,
        timezoneOffset: new Date().getTimezoneOffset(),
      };

      const response = await analysisService.getAveragePriceAnalysis(params);
      setAveragePrice(response.averagePrice);
    } catch (error) {
      console.error('Failed to fetch average price:', error);
      setAveragePrice(0);
    } finally {
      setAveragePriceLoading(false);
    }
  };

  const fetchPriceChange = async () => {
    if (!selectedBasket) return;
    try {
      setPriceChangeLoading(true);

      const params = {
        locationIds: selectedLocationIds,
        basketId: selectedBasket || undefined,
        startTime: dateRange[0].valueOf(),
        endTime: dateRange[1].valueOf(),
        productIds: selectedPresentations || undefined,
        timezoneOffset: new Date().getTimezoneOffset(),
      };
      const response = await analysisService.getPriceChange(params);
      setPriceChange(response.percentageChange);
      setPriceChangeDirection(response.changeDirection);
    } catch (error) {
      console.error('Failed to fetch price change:', error);
      setPriceChange(0);
      setPriceChangeDirection('no-change');
    } finally {
      setPriceChangeLoading(false);
    }
  };

  const fetchInspectionCount = async () => {
    if (!selectedBasket) return;
    try {
      setInspectionCountLoading(true);

      const response = await inspectionService.getInspectionCount(
        dateRange[0].valueOf(),
        dateRange[1].add(1, 'day').valueOf(),
        selectedBasket || undefined,
        // selectedMunicipality || undefined,
        selectedLocationIds,
        selectedPresentations || undefined,
        new Date().getTimezoneOffset()
      );
      setInspectionCount(response.count);
    } catch (error) {
      console.error('Failed to fetch inspection count:', error);
      setInspectionCount(0);
    } finally {
      setInspectionCountLoading(false);
    }
  };

  const fetchInflationData = async () => {
    if (!selectedBasket) return;
    try {
      setInflationLoading(true);

      const params = {
        startTime: dateRange[0].valueOf(),
        endTime: dateRange[1].valueOf(),
        limit: 10,
        basketId: selectedBasket || undefined,
        locationIds: selectedLocationIds,
        productIds: selectedPresentations || undefined,
        timezoneOffset: new Date().getTimezoneOffset(),
      };

      const response = await analysisService.getProductsWithPriceChange(params, 'inflation');

      setInflationProducts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch inflation data:', error);
      setInflationProducts([]);
    } finally {
      setInflationLoading(false);
    }
  };

  const fetchDeflationData = async () => {
    if (!selectedBasket) return;
    try {
      setDeflationLoading(true);

      const params = {
        startTime: dateRange[0].valueOf(),
        endTime: dateRange[1].valueOf(),
        limit: 10,
        basketId: selectedBasket || undefined,
        locationIds: selectedLocationIds,
        productIds: selectedPresentations || undefined,
        timezoneOffset: new Date().getTimezoneOffset(),
      };

      const response = await analysisService.getProductsWithPriceChange(params, 'deflation');

      setDeflationProducts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch deflation data:', error);
      setDeflationProducts([]);
    } finally {
      setDeflationLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedBasket) {
      return;
    }

    if (
      selectedPresentations?.length > 0 ||
      selectedMunicipality ||
      selectedDepartment ||
      selectedBasket !== defaultBasketId ||
      dateRange[0].valueOf() !== dayjs().subtract(7, 'day').startOf('day').valueOf() ||
      dateRange[1].valueOf() !== dayjs().endOf('day').valueOf()
    ) {
      setEnableClearFiltersBtn(true);
    } else {
      setEnableClearFiltersBtn(false);
    }
    fetchAveragePrice();
    fetchPriceChange();
    fetchInspectionCount();
    fetchInflationData();
    fetchDeflationData();
    calculateLastPeriod();
  }, [selectedBasket, selectedDepartment, selectedMunicipality, dateRange, selectedPresentations]);

  const removeDuplicate = (arr: Record<string, unknown>[], property: string) => {
    return [...new Map(arr.map((item) => [item[property], item])).values()];
  };

  const handleDepartmentChange = (department: string) => {
    setSelectedDepartment(department);
    setSelectedMunicipality('');

    if (department) {
      try {
        setMunicipalityLoading(true);
        const locationIdsForSelectedDepartment = locations
          ?.filter((location) => location?.district === department)
          ?.map((location) => location?.id);

        setSelectedLocationIds(locationIdsForSelectedDepartment);
        const filteredMunicipalities = locations?.filter(
          (location) => location?.district === department
        );
        setMunicipalities(filteredMunicipalities);
      } catch (error) {
        console.error('Failed to load municipalities:', error);
      } finally {
        setMunicipalityLoading(false);
      }
    } else {
      setMunicipalities([]);
      setSelectedMunicipality(undefined);
      setSelectedLocationIds([]);
    }
  };

  const handleMunicipalityChange = (municipality: string) => {
    if (municipality && selectedDepartment) {
      const locationIdsForSelectedMunicipality = locations
        ?.filter(
          (location) => location?.district === selectedDepartment && location.city === municipality
        )
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipality);
    } else if (municipality) {
      const locationIdsForSelectedMunicipality = locations
        ?.filter((location) => location?.city === municipality)
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipality);
    } else if (selectedDepartment) {
      const locationIdsForSelectedDepartment = locations
        ?.filter((location) => location?.district === selectedDepartment)
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedDepartment);
    } else {
      setSelectedLocationIds([]);
    }

    setSelectedMunicipality(municipality);
  };

  const handleBasketChange = (value: number | undefined, option: any) => {
    setSelectedBasket(value);
    setSelectedBasketLabel(option?.label ?? '');
    setSelectedPresentations([]);
  };

  // const loadPresentationsForSelection = useCallback(async () => {
  const loadPresentationsForSelection = async () => {
    try {
      const allPresentations = await referenceDataSyncService.getPresentationsForFilters(
        selectedBasket || undefined
      );

      const presentationOptionArr = allPresentations.map((presentation: any) => ({
        label: presentation?.name,
        value: presentation?.id,
        id: presentation?.id,
      }));

      setPresentations(presentationOptionArr);
    } catch (error) {
      console.error('Error loading presentations for selection:', error);
      setPresentations([]);
    }
  };

  useEffect(() => {
    loadPresentationsForSelection();
  }, [selectedBasket]);

  const getItems: any = [
    {
      label: t('priceVariationTrends'),
      key: '1',
      children: <OverviewTab t={t} />,
    },
    {
      label: t('basketsAveragePrice'),
      key: '2',
      children: <TrendExplorerTab t={t} />,
    },
    {
      label: t('priceComparison'),
      key: '3',
      children: <ComparativeTab t={t} />,
    },
    {
      label: t('priceMap'),
      key: '4',
      children: <PriceMapTab t={t} />,
    },
    {
      label: t('productPriceAnalytics'),
      key: '5',
      children: <ProductPriceAnalyticsTab t={t} />,
    },
    hasPermission([PermissionEnum.UPLOAD_EXTERNAL_DATA]) && {
      label: t('externalDataUpload'),
      key: '6',
      children: <ExternalDataUploadTab activeTab={activeTab} />,
      forceRender: true,
    },
  ];

  return (
    <>
      <section className="mb-2 ml-2 mt-12 flex w-full flex-col justify-start gap-4 md:mb-16 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h1 className="text-[48px] text-headings-font-color sm:text-[64px]">
            {t('pageHeading')}
          </h1>
          <p className="text-base font-medium text-font-color">{t('pageDescription')}</p>
        </div>
        <div className="flex justify-end gap-4">
          {hasPermission([
            PermissionEnum.ANALYTICS,
            PermissionEnum.MANAGE_ANALYTICS,
            PermissionEnum.VIEW_ANALYTICS,
          ]) && (
              <PrimaryButton
                handleOnClick={() => {
                  navigate('/analysis/advanced');
                }}
                disabled={!isOnline}
              >
                <span className="px-4 py-1">{t('viewAdvancedAnalysis')} →</span>
              </PrimaryButton>
            )}
          {hasPermission([
            PermissionEnum.RUN_INSPECTIONS,
            PermissionEnum.CREATE_INSPECTIONS,
            PermissionEnum.UPDATE_INSPECTIONS,
            PermissionEnum.VIEW_INSPECTIONS,
            PermissionEnum.DELETE_INSPECTIONS,
          ]) && (
              <PrimaryButton
                handleOnClick={() => {
                  navigate('/inspector');
                }}
                disabled={!isOnline}
              >
                <span className="px-4 py-1">{t('manageTasks')} →</span>
              </PrimaryButton>
            )}
        </div>
      </section>

      <div className="my-6 w-full rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        {/* filter section */}
        <section className="mb-8 w-full">
          <div className="">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{t('filters')}</h3>
              <Button
                type="link"
                onClick={clearAllFilters}
                className="hover:text-primary-dark text-sm text-primary"
                disabled={!enableClearFiltersBtn}
              >
                {t('clearFilters')}
              </Button>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('dateFilter')}</label>
                </div>
                <DatePicker.RangePicker
                  className="w-full [&>.ant-picker]:!rounded-lg"
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                  allowClear={false}
                  disabledDate={disableFutureDates}
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('basket')}</label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('basketPlaceholder')}
                  showSearch
                  loading={loading}
                  value={selectedBasket}
                  onChange={handleBasketChange}
                  filterOption={(input, option) =>
                    normalize(option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(normalize(input.toLowerCase()))
                  }
                  options={baskets.map((basket) => ({
                    label: basket.name,
                    value: basket.id,
                  }))}
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('presentations')}</label>
                </div>
                <Select
                  className="z-100 w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('presentationsPlaceholder')}
                  showSearch
                  mode="multiple"
                  maxTagCount={1}
                  maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 25 : 14}
                  loading={loading}
                  onClear={() => setSelectedPresentations([])}
                  value={selectedPresentations}
                  onChange={setSelectedPresentations}
                  options={presentations}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                />
                <div style={{ display: 'flex', marginBottom: '0px' }}>
                  {selectedPresentations?.length > 0 && (
                    <a
                      className="ml-auto"
                      onClick={() => {
                        setSelectedPresentations([]);
                      }}
                    >
                      {t('basket:deselectAll')}
                    </a>
                  )}
                </div>
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('department')}</label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('departmentPlaceholder')}
                  allowClear
                  showSearch
                  loading={locationLoading}
                  onClear={() => handleDepartmentChange}
                  value={selectedDepartment}
                  onChange={handleDepartmentChange}
                  options={removeDuplicate(
                    locations.map((location) => ({
                      label: location.district,
                      value: location.district,
                    })),
                    'value'
                  )}
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={4}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('municipality')}</label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('municipalityPlaceholder')}
                  allowClear
                  onClear={() => setSelectedMunicipality(undefined)}
                  value={selectedMunicipality || undefined}
                  onChange={handleMunicipalityChange}
                  disabled={!selectedDepartment || municipalities.length === 0}
                  options={municipalities.map((location) => ({
                    label: location.city,
                    value: location.city,
                  }))}
                  loading={municipalityLoading}
                  showSearch
                  filterOption={(input, option) =>
                    normalize(option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(normalize(input.toLowerCase()))
                  }
                />
              </Col>
            </Row>
          </div>
        </section>

        <section className="z-10 my-6 grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <StatCard
            isLoading={averagePriceLoading}
            value={averagePriceLoading ? 0 : averagePrice}
            icon={<Cart />}
            title={
              selectedBasketName
                ? t('avgPrice', { basketName: selectedBasketName })
                : t('avgPriceNoBasicBasket')
            }
            prefix={'L. '}
          />
          <StatCard
            isLoading={priceChangeLoading}
            value={priceChangeLoading ? 0 : priceChange}
            icon={<PercentageOutlined />}
            title={t('%changeVsLastPeriod')}
            info={t('changeVsLastPeriodInfo', {
              from: previousPeriodStartDate,
              to: previousPeriodEndDate,
              fromFilter: dayjs(dateRange[0]).format('YYYY-MM-DD'),
              toFilter: dayjs(dateRange[1]).format('YYYY-MM-DD'),
            })}
            // prefix={priceChangeDirection === 'increase' ? '+' : ''}
            postfix={
              <>
                {priceChangeDirection === 'increase' ? (
                  // <FaArrowTrendUp className="ml-2" />
                  <PercentageOutlined className="text-md ml-2" />
                ) : priceChangeDirection === 'decrease' ? (
                  // <FaArrowTrendDown className="ml-2" />
                  <PercentageOutlined className="text-md ml-2" />
                ) : null}
              </>
            }
            statColor={
              priceChangeDirection === 'decrease'
                ? '#17A34A'
                : priceChangeDirection === 'increase'
                  ? '#EF4444'
                  : '#6B7280'
            }
          />
          <StatCard
            isLoading={inspectionCountLoading}
            value={inspectionCountLoading ? 0 : inspectionCount}
            icon={<VscInspect />}
            title={t('totalInspections')}
          />
        </section>

        <section className="mb-6 mt-6 w-full">
          <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 md:gap-8 lg:gap-12">
            <AnalysisListCard
              title={t('productsWithHighestPriceVolatilityInflation')}
              subTilte={t('topProductsShowingInflation', {
                inflationType: t('inflation'),
                from: previousPeriodStartDate,
                to: previousPeriodEndDate,
                fromFilter: dayjs(dateRange[0]).format('YYYY-MM-DD'),
                toFilter: dayjs(dateRange[1]).format('YYYY-MM-DD'),
              })}
              list={
                inflationLoading
                  ? [
                    // <div key="loading" className="py-4 text-center text-sm text-gray-500">
                    //   {t('loading')}
                    // </div>,
                    <div className="flex w-full flex-row items-center !justify-center">
                      <Spin size="large" />
                    </div>,
                  ]
                  : inflationProducts.length > 0
                    ? inflationProducts.map((presentation, index) => (
                      <InfoCard
                        key={index}
                        title={presentation.productName || t('unknownProduct')}
                        category={
                          presentation.baskets && presentation.baskets.length > 0
                            ? presentation.baskets.map((basket) => basket.name).join(', ')
                            : selectedBasketName || t('unknownBasket')
                        }
                        description={t('vsLastPeriod')}
                        changePerc={presentation.priceChangePercentage || 0}
                      />
                    ))
                    : [
                      <div key="no-data" className="py-8 text-center text-sm text-gray-500">
                        {t('noDataAvailable')}
                      </div>,
                    ]
              }
            />

            <AnalysisListCard
              title={t('productsWithHighestPriceVolatilityDeflation')}
              subTilte={t('topProductsShowingInflation', {
                inflationType: t('deflation'),
                from: previousPeriodStartDate,
                to: previousPeriodEndDate,
                fromFilter: dayjs(dateRange[0]).format('YYYY-MM-DD'),
                toFilter: dayjs(dateRange[1]).format('YYYY-MM-DD'),
              })}
              list={
                deflationLoading
                  ? [
                    // <div key="loading" className="py-4 text-center text-sm text-gray-500">
                    //   {t('loading')}
                    // </div>,
                    <div className="flex w-full flex-row items-center !justify-center">
                      <Spin size="large" />
                    </div>,
                  ]
                  : deflationProducts.length > 0
                    ? deflationProducts.map((presentation, index) => (
                      <InfoCard
                        key={index}
                        title={presentation.productName || t('unknownProduct')}
                        category={
                          presentation.baskets && presentation.baskets.length > 0
                            ? presentation.baskets.map((basket) => basket.name).join(', ')
                            : selectedBasketName || t('unknownBasket')
                        }
                        description={t('vsLastPeriod')}
                        changePerc={presentation.priceChangePercentage || 0}
                      />
                    ))
                    : [
                      <div key="no-data" className="py-8 text-center text-sm text-gray-500">
                        {t('noDataAvailable')}
                      </div>,
                    ]
              }
            />
          </div>
        </section>
      </div>

      <section className="w-full sm:mt-2 md:mt-6">
        <CustomTabs
          defaultActiveKey="1"
          items={getItems.filter(Boolean)}
          activeKey={activeTab}
          onChange={handleTabChange}
          className="responsive-kebab-tabs"
        />
      </section>
    </>
  );
};

export default AnalysisAndReports;
